// src/pages/admin/OrganizationsPanel.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Collapse,
  IconButton,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import EditCalendarRoundedIcon from "@mui/icons-material/EditCalendarRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";

import { listOrganizations, updateOrgSubscription, clearAllClientData, resendCredentials } from "../../api/adminService";
import ReportGeneratorDialog from "../../components/organisms/ReportGeneratorDialog";

const PLAN_OPTIONS = ["", "starter", "team", "growth"];

const planChipColor = (plan) => {
  switch (plan) {
    case "starter": return "default";
    case "team": return "primary";
    case "growth": return "success";
    default: return "default";
  }
};

const subStatusColor = (status) => {
  switch (status) {
    case "active": return "success";
    case "trialing": return "info";
    case "canceled": return "error";
    case "past_due": return "warning";
    default: return "default";
  }
};

function MembersRow({ members }) {
  const [resendTarget, setResendTarget] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState("");
  const [resendDone, setResendDone] = useState(false);

  const handleOpenResend = (member) => {
    setResendTarget(member);
    setResendError("");
    setResendDone(false);
  };

  const handleCloseResend = () => {
    if (resendLoading) return;
    setResendTarget(null);
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendError("");
    try {
      await resendCredentials(resendTarget.id);
      setResendDone(true);
    } catch (e) {
      setResendError(e.message);
    } finally {
      setResendLoading(false);
    }
  };

  if (!members || members.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No members found.
      </Typography>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Active</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Trial Ends</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((m) => (
            <TableRow key={m.id}>
              <TableCell sx={{ fontSize: 13 }}>{m.name || "—"}</TableCell>
              <TableCell sx={{ fontSize: 13 }}>{m.email}</TableCell>
              <TableCell>
                <Chip
                  label={m.role}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: 11, textTransform: "capitalize" }}
                />
              </TableCell>
              <TableCell>
                {m.subscription_status ? (
                  <Chip
                    label={m.subscription_status}
                    color={subStatusColor(m.subscription_status)}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: 11, textTransform: "capitalize" }}
                  />
                ) : "—"}
              </TableCell>
              <TableCell>
                <Chip
                  label={m.is_active ? "Yes" : "No"}
                  color={m.is_active ? "success" : "default"}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: 11 }}
                />
              </TableCell>
              <TableCell sx={{ fontSize: 13 }}>
                {m.trial_ends_at
                  ? new Date(m.trial_ends_at).toLocaleDateString()
                  : "—"}
              </TableCell>
              <TableCell>
                <Tooltip title="Resend credentials">
                  <IconButton size="small" color="primary" onClick={() => handleOpenResend(m)}>
                    <MarkEmailReadRoundedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Resend Credentials Dialog */}
      <Dialog open={Boolean(resendTarget)} onClose={handleCloseResend} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockResetRoundedIcon color="primary" />
          Resend Credentials
        </DialogTitle>
        <DialogContent>
          {resendDone ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              New credentials sent to <strong>{resendTarget?.email}</strong>.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                This will:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li" variant="body2">Generate a new random password</Typography>
                <Typography component="li" variant="body2">Invalidate the current password immediately</Typography>
                <Typography component="li" variant="body2">
                  Send login credentials to <strong>{resendTarget?.email}</strong>
                </Typography>
              </Box>
              {resendError && <Alert severity="error" sx={{ mt: 2 }}>{resendError}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!resendDone ? (
            <>
              <Button onClick={handleCloseResend} disabled={resendLoading}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleResend}
                disabled={resendLoading}
                startIcon={resendLoading ? <CircularProgress size={14} color="inherit" /> : <MarkEmailReadRoundedIcon />}
              >
                {resendLoading ? "Sending…" : "Send credentials"}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseResend} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

function OrgRow({ row, onReport, onEditTrial, onClearData }) {
  const [open, setOpen] = useState(false);
  const seatPct = row.seat_limit > 0
    ? Math.min((row.member_count / row.seat_limit) * 100, 100)
    : 0;

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: open ? "none" : undefined } }}>
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
        <TableCell>
          {row.subscription_plan ? (
            <Chip
              label={row.subscription_plan}
              color={planChipColor(row.subscription_plan)}
              size="small"
              sx={{ fontWeight: 600, textTransform: "capitalize" }}
            />
          ) : "—"}
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={seatPct}
              sx={{
                width: 60,
                height: 6,
                borderRadius: 3,
                bgcolor: "grey.200",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  bgcolor: seatPct >= 100 ? "error.main" : seatPct >= 80 ? "warning.main" : "success.main",
                },
              }}
            />
            <Typography variant="caption" fontWeight={600}>
              {row.member_count}/{row.seat_limit}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          {row.market_city
            ? `${row.market_city}, ${row.market_state}`
            : "—"}
        </TableCell>
        <TableCell>
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString()
            : "—"}
        </TableCell>
        <TableCell sx={{ width: 100 }}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Extend Trial / Edit Subscription">
              <IconButton size="small" color="warning" onClick={() => onEditTrial(row)}>
                <EditCalendarRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Generate Coverage Report">
              <IconButton size="small" color="primary" onClick={() => onReport(row)}>
                <AssessmentRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear All Client Data">
              <IconButton size="small" sx={{ color: "error.main" }} onClick={() => onClearData(row)}>
                <DeleteForeverRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, px: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2, bgcolor: "action.hover" }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Members ({row.member_count})
              </Typography>
              <MembersRow members={row.members} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function OrganizationsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportOrg, setReportOrg] = useState(null);

  const handleOpenReport = (org) => {
    setReportOrg(org);
    setReportDialogOpen(true);
  };

  // Clear all data dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearOrg, setClearOrg] = useState(null);
  const [clearCode, setClearCode] = useState("");
  const [clearInput, setClearInput] = useState("");
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState("");
  const [clearSuccess, setClearSuccess] = useState(null);

  const handleOpenClearData = (org) => {
    setClearOrg(org);
    setClearCode(String(Math.floor(100000 + Math.random() * 900000)));
    setClearInput("");
    setClearError("");
    setClearSuccess(null);
    setClearDialogOpen(true);
  };

  const handleCloseClearDialog = () => {
    if (clearLoading) return;
    setClearDialogOpen(false);
  };

  const handleExecuteClear = async () => {
    setClearLoading(true);
    setClearError("");
    try {
      const result = await clearAllClientData(clearOrg.id);
      setClearSuccess(result);
      fetchData();
    } catch (e) {
      setClearError(e.message);
    } finally {
      setClearLoading(false);
    }
  };

  // Trial edit dialog state
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialOrg, setTrialOrg] = useState(null);
  const [trialDate, setTrialDate] = useState("");
  const [trialStatus, setTrialStatus] = useState("");
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialError, setTrialError] = useState("");
  const [trialSuccess, setTrialSuccess] = useState("");

  const handleOpenEditTrial = (org) => {
    setTrialOrg(org);
    // Pre-fill with current trial end from first member that has it
    const firstMemberWithTrial = (org.members || []).find((m) => m.trial_ends_at);
    setTrialDate(
      firstMemberWithTrial
        ? firstMemberWithTrial.trial_ends_at.slice(0, 10)
        : ""
    );
    setTrialStatus("");
    setTrialError("");
    setTrialSuccess("");
    setTrialDialogOpen(true);
  };

  const handleSaveTrial = async () => {
    if (!trialDate && !trialStatus) return;
    setTrialSaving(true);
    setTrialError("");
    setTrialSuccess("");
    try {
      const payload = {};
      if (trialDate) payload.trial_ends_at = `${trialDate}T23:59:59`;
      if (trialStatus) payload.subscription_status = trialStatus;
      const result = await updateOrgSubscription(trialOrg.id, payload);
      setTrialSuccess(`Updated ${result.users_updated} user(s) in ${result.org_name}.`);
      fetchData();
    } catch (e) {
      setTrialError(e.message);
    } finally {
      setTrialSaving(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listOrganizations({
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
        subscription_plan: planFilter || undefined,
      });
      setRows(data.organizations || []);
      setTotal(data.total ?? 0);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, planFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce search: reset page when filters change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handlePlanChange = (e) => {
    setPlanFilter(e.target.value);
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Organizations
      </Typography>

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <TextField
          size="small"
          select
          label="Plan"
          value={planFilter}
          onChange={handlePlanChange}
          sx={{ minWidth: 150 }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">All Plans</MenuItem>
          {PLAN_OPTIONS.filter(Boolean).map((p) => (
            <MenuItem key={p} value={p}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 700 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Seats</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Market</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No organizations found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => <OrgRow key={row.id} row={row} onReport={handleOpenReport} onEditTrial={handleOpenEditTrial} onClearData={handleOpenClearData} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      {/* Coverage Report Dialog */}
      <ReportGeneratorDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        organization={reportOrg}
      />

      {/* Clear All Client Data Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleCloseClearDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
          <WarningAmberRoundedIcon />
          Clear all data — {clearOrg?.name}
        </DialogTitle>
        <DialogContent>
          {clearSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Deleted: <strong>{clearSuccess.deleted_clients}</strong> clients
              and <strong>{clearSuccess.deleted_deals}</strong> deals.
            </Alert>
          ) : (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                This action is <strong>permanent and irreversible</strong>. It cannot be undone.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                The following data will be permanently deleted from <strong>{clearOrg?.name}</strong>:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2.5 }}>
                <Typography component="li" variant="body2">All <strong>clients</strong> and their activity history</Typography>
                <Typography component="li" variant="body2">All <strong>deals</strong>, properties, photo appointments, and open houses</Typography>
                <Typography component="li" variant="body2">Alert events, notes, tasks, and associated listings</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Type the code below to confirm:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 1.5,
                  py: 1.5,
                  px: 3,
                  border: "2px dashed",
                  borderColor: "error.main",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ letterSpacing: 6, fontFamily: "monospace", color: "error.main" }}
                >
                  {clearCode}
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter the code here"
                value={clearInput}
                onChange={(e) => setClearInput(e.target.value)}
                disabled={clearLoading}
                inputProps={{ style: { fontFamily: "monospace", letterSpacing: 4, fontSize: 20, textAlign: "center" } }}
                autoFocus
              />
              {clearError && <Alert severity="error" sx={{ mt: 2 }}>{clearError}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!clearSuccess ? (
            <>
              <Button onClick={handleCloseClearDialog} disabled={clearLoading}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleExecuteClear}
                disabled={clearLoading || clearInput !== clearCode}
              >
                {clearLoading ? "Deleting…" : "Delete all data"}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseClearDialog} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Trial / Subscription Edit Dialog */}
      <Dialog
        open={trialDialogOpen}
        onClose={() => !trialSaving && setTrialDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Edit Trial — {trialOrg?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changes apply to <strong>all members</strong> of this organization.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Trial ends on"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={trialDate}
              onChange={(e) => setTrialDate(e.target.value)}
              helperText="Leave blank to keep current date"
            />
            <TextField
              select
              label="Subscription status (optional)"
              fullWidth
              value={trialStatus}
              onChange={(e) => setTrialStatus(e.target.value)}
              helperText="Leave blank to keep current status"
            >
              <MenuItem value="">— No change —</MenuItem>
              <MenuItem value="trialing">Trialing</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
              <MenuItem value="past_due">Past Due</MenuItem>
            </TextField>

            {trialError && <Alert severity="error">{trialError}</Alert>}
            {trialSuccess && <Alert severity="success">{trialSuccess}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialDialogOpen(false)} disabled={trialSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTrial}
            disabled={trialSaving || (!trialDate && !trialStatus)}
          >
            {trialSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
